import React, { useState, useEffect, useRef } from 'react';
import { 
  QrCode, Download, Copy, Check, Radio, 
  Printer, Share2, Globe, Lock, ExternalLink,
  Smartphone, Volume2, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://groovie-business-api-production.up.railway.app/api/v1';
const GROOVIE_LIVE_URL = 'https://groovie.live';

// Simple QR Code generator using external API
const generateQRCodeURL = (data, size = 300) => {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}&bgcolor=ffffff&color=000000&margin=10`;
};

export default function QRCodeGenerator() {
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [qrSize, setQrSize] = useState(300);
  const [showDropdown, setShowDropdown] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/channels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const channelList = data.channels || [];
        setChannels(channelList);
        if (channelList.length > 0) {
          setSelectedChannel(channelList[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChannelURL = (channel) => {
    // Q4: unified share/QR shape — the consumer app deep-links a station at /listen/{channelId}
    // (survives Q5's move of the app to /listen). Only PUBLIC stations are shareable; private
    // channels return no URL (we show an honest notice instead of a dead link).
    if (!channel || !channel.is_public) return '';
    return `${GROOVIE_LIVE_URL}/listen/${channel.id}`;
  };

  const handleCopyLink = async () => {
    if (!selectedChannel) return;
    
    try {
      await navigator.clipboard.writeText(getChannelURL(selectedChannel));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadQR = async () => {
    if (!selectedChannel) return;
    
    const qrUrl = generateQRCodeURL(getChannelURL(selectedChannel), 500);
    
    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `groovie-qr-${selectedChannel.name.toLowerCase().replace(/\s+/g, '-')}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download:', err);
      // Fallback: open in new tab
      window.open(qrUrl, '_blank');
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !selectedChannel) return;

    const qrUrl = generateQRCodeURL(getChannelURL(selectedChannel), 400);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Groovie QR Code - ${selectedChannel.name}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 40px;
              box-sizing: border-box;
            }
            .container {
              text-align: center;
              max-width: 500px;
            }
            .logo {
              font-size: 32px;
              font-weight: bold;
              color: #7c3aed;
              margin-bottom: 10px;
            }
            .tagline {
              color: #64748b;
              margin-bottom: 30px;
            }
            .qr-code {
              border: 2px solid #e2e8f0;
              border-radius: 16px;
              padding: 20px;
              margin-bottom: 20px;
            }
            .qr-code img {
              width: 300px;
              height: 300px;
            }
            .channel-name {
              font-size: 24px;
              font-weight: 600;
              color: #1e293b;
              margin-bottom: 10px;
            }
            .instructions {
              color: #64748b;
              font-size: 14px;
              line-height: 1.6;
            }
            .url {
              color: #7c3aed;
              font-size: 12px;
              margin-top: 20px;
              word-break: break-all;
            }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="logo">🎵 Groovie</div>
            <div class="tagline">Stay in the Groove</div>
            <div class="qr-code">
              <img src="${qrUrl}" alt="QR Code" />
            </div>
            <div class="channel-name">${selectedChannel.name}</div>
            <div class="instructions">
              Scan this QR code with your phone<br/>
              to listen to our radio station
            </div>
            <div class="url">${getChannelURL(selectedChannel)}</div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleShare = async () => {
    if (!selectedChannel) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Listen to ${selectedChannel.name} on Groovie`,
          text: `Tune in to ${selectedChannel.name} - powered by Groovie AI Radio`,
          url: getChannelURL(selectedChannel)
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (channels.length === 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <QrCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Channels Yet</h3>
          <p className="text-gray-600 mb-6">Create a channel first to generate QR codes for your listeners.</p>
          <a
            href="/channels"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Radio className="w-5 h-5" />
            Create a Channel
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">QR Codes</h1>
        <p className="text-gray-600 mt-1">Generate QR codes for your listeners to easily connect to your stations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* QR Code Preview */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8">
          {/* Channel Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Channel</label>
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Radio className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{selectedChannel?.name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {selectedChannel?.is_public ? (
                        <>
                          <Globe className="w-3 h-3" />
                          <span>Public</span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-3 h-3" />
                          <span>Private</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                  {channels.map(channel => (
                    <button
                      key={channel.id}
                      onClick={() => {
                        setSelectedChannel(channel);
                        setShowDropdown(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 ${
                        selectedChannel?.id === channel.id ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        selectedChannel?.id === channel.id ? 'bg-purple-200' : 'bg-gray-100'
                      }`}>
                        <Radio className={`w-4 h-4 ${
                          selectedChannel?.id === channel.id ? 'text-purple-600' : 'text-gray-500'
                        }`} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{channel.name}</p>
                        <p className="text-xs text-gray-500">{channel.is_public ? 'Public' : 'Private'}</p>
                      </div>
                      {selectedChannel?.id === channel.id && (
                        <Check className="w-4 h-4 text-purple-600 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* QR Code Display */}
          <div ref={qrRef} className="flex flex-col items-center p-8 bg-white border-2 border-gray-100 rounded-2xl">
            <div className="text-center mb-4">
              <span className="text-2xl">🎵</span>
              <span className="font-bold text-purple-600 ml-1">Groovie</span>
            </div>
            
            {selectedChannel?.is_public ? (
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <img
                  src={generateQRCodeURL(getChannelURL(selectedChannel), qrSize)}
                  alt="QR Code"
                  className="w-64 h-64"
                />
              </div>
            ) : (
              <div className="w-64 h-64 flex flex-col items-center justify-center text-center bg-gray-50 border border-dashed border-gray-200 rounded-xl px-6">
                <Lock className="w-8 h-8 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-700">Sharing is available for public stations</p>
                <p className="text-xs text-gray-500 mt-2">Make this channel public (Channels &rarr; visibility) to generate a shareable link and QR code.</p>
              </div>
            )}

            <p className="font-semibold text-gray-900 mt-4 text-lg">{selectedChannel?.name}</p>
            <p className="text-gray-500 text-sm mt-1">{selectedChannel?.is_public ? 'Scan to listen' : 'Public stations only'}</p>
          </div>

          {/* Link Preview (public stations only) */}
          {selectedChannel?.is_public && (
            <div className="mt-4 p-3 bg-gray-50 rounded-xl flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={getChannelURL(selectedChannel)}
                className="flex-1 bg-transparent text-sm text-gray-600 outline-none"
              />
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-gray-500" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Actions & Instructions */}
        <div className="space-y-6">
          {/* Actions (public stations only — private channels have no shareable link) */}
          {selectedChannel?.is_public && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleDownloadQR}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700"
              >
                <Download className="w-5 h-5" />
                Download PNG
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
              >
                <Printer className="w-5 h-5" />
                Print
              </button>
              <button
                onClick={handleShare}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
              >
                <Share2 className="w-5 h-5" />
                Share
              </button>
              <a
                href={getChannelURL(selectedChannel)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
              >
                <ExternalLink className="w-5 h-5" />
                Preview
              </a>
            </div>
          </div>
          )}

          {/* Usage Ideas */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border border-purple-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">💡 Where to Use QR Codes</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Table Tents & Signage</p>
                  <p className="text-sm text-gray-600">Place on tables, walls, or checkout counters</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Receipts & Packaging</p>
                  <p className="text-sm text-gray-600">Add to receipts, bags, or product packaging</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Social Media</p>
                  <p className="text-sm text-gray-600">Share the QR code in posts and stories</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">4</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Employee Badges</p>
                  <p className="text-sm text-gray-600">Let staff help customers connect</p>
                </div>
              </li>
            </ul>
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">How It Works</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-600">Scan QR</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200" />
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Globe className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-sm text-gray-600">Opens Groovie</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200" />
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-gray-600">Listens!</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
